"use client";

import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, FileCheck2, Loader2, Play, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "../../ui/dropdown-menu";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { formatLeaseDialogCell } from "@/utils/lease-dialog-format";
import { computeJournalEntry, type JournalEntryResult } from "@/utils/leaseReassessmentJournalEntry";

interface LeaseReassessmentIndASProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface AssessmentLease {
  lease_name: string;
  commencement_date: string;
  lease_period: number;
  int_ror: number;
  payment_cycle: string;
}

interface PaymentDetailRow {
  payment_classification: string;
  payment_type: string;
  payment_amount: number;
  n_payments: number;
  int_incr_text: string;
  incr_per_text: string;
  month_first_payment: number;
}

interface ReassessmentRow {
  id: string;
  lease_name: string;
  factors_changed: string[];
  lease_commencement_date: string;
  lease_reassessment_date: string;
  revised_int_ror: number;
  original_period: number;
  total_revised_period: number;
  payment_details: PaymentDetailRow[];
}

interface OutputLease {
  lease_info: Record<string, any>;
  payment_details: Array<Record<string, any>>;
  schedule: Array<Record<string, any>>;
}

const ASSESSMENT_INPUT_FILE = "Execution_LEASE_AssessmentInp.json";
const REASSESSMENT_INPUT_FILE = "Execution_LEASE_ReassessmentInp.json";
const REASSESSMENT_OUTPUT_FILE = "Execution_LEASE_Reassessment.json";
const ASSESSMENT_OUTPUT_FILE = "Execution_LEASE_AssessmentOut.json";
const MODIFICATION_OUTPUT_FILE = "Execution_LEASE_Modification.json";
const FACTOR_OPTIONS = ["Lease Period", "Purchase Option", "Guaranteed Residual Value", "Variable Payment"];
const NON_APPLICABLE_CLASSIFICATIONS = new Set(["Guaranteed Residual Value", "Purchase Price", "Penalty for Termination"]);

const defaultPaymentRow = (): PaymentDetailRow => ({
  payment_classification: "",
  payment_type: "",
  payment_amount: 0,
  n_payments: 0,
  int_incr_text: "",
  incr_per_text: "",
  month_first_payment: 0,
});

export default function LeaseReassessmentIndAS({ setActiveSection }: LeaseReassessmentIndASProps) {
  const [assessmentLeases, setAssessmentLeases] = useState<AssessmentLease[]>([]);
  const [rows, setRows] = useState<ReassessmentRow[]>([]);
  const [outputLeases, setOutputLeases] = useState<OutputLease[]>([]);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [isRunning, setIsRunning] = useState(false);
  const [selectedOutputLease, setSelectedOutputLease] = useState<OutputLease | null>(null);
  const [isOutputDialogOpen, setIsOutputDialogOpen] = useState(false);
  const [isJournalDialogOpen, setIsJournalDialogOpen] = useState(false);
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalResult, setJournalResult] = useState<JournalEntryResult | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const assessmentResult = await (window as any).sharePointAPI?.downloadClientJson?.(ASSESSMENT_INPUT_FILE);
        const leases = Array.isArray(assessmentResult?.data?.leases) ? assessmentResult.data.leases : [];
        const baseLeases: AssessmentLease[] = leases.map((lease: any) => ({
          lease_name: lease.lease_name || "",
          commencement_date: lease.commencement_date || "",
          lease_period: Number(lease.lease_period) || 0,
          int_ror: Number(lease.int_ror) || 0,
          payment_cycle: lease.payment_cycle || "Monthly",
        }));
        setAssessmentLeases(baseLeases);

        const reassessmentResult = await (window as any).sharePointAPI?.downloadClientJson?.(REASSESSMENT_INPUT_FILE);
        const savedRows = Array.isArray(reassessmentResult?.data?.rows) ? reassessmentResult.data.rows : [];
        const hydratedRows: ReassessmentRow[] = savedRows.map((row: any, index: number) => ({
          id: `row_${Date.now()}_${index}`,
          lease_name: row.lease_name || "",
          factors_changed: Array.isArray(row.factors_changed) ? row.factors_changed : [],
          lease_commencement_date: toInputDate(row.lease_commencement_date || ""),
          lease_reassessment_date: toInputDate(row.lease_reassessment_date || ""),
          revised_int_ror: Number(row.revised_int_ror) || 0,
          original_period: Number(row.original_period) || 0,
          total_revised_period: Number(row.total_revised_period) || 0,
          payment_details: Array.isArray(row.payment_details) && row.payment_details.length > 0
            ? row.payment_details.map((p: any) => ({
                payment_classification: p.payment_classification || "",
                payment_type: p.payment_type || "",
                payment_amount: Number(p.payment_amount) || 0,
                n_payments: Number(p.n_payments) || 0,
                int_incr_text: Array.isArray(p.int_incr) ? p.int_incr.join(",") : "",
                incr_per_text: Array.isArray(p.incr_per) ? p.incr_per.join(",") : "",
                month_first_payment: Number(p.month_first_payment) || 0,
              }))
            : [defaultPaymentRow()],
        }));
        setRows(hydratedRows);
      } catch (error) {
        console.warn("[LeaseReassessmentIndAS] Failed to load data:", error);
      }
    };
    loadData();
    loadOutput();
  }, []);

  const leaseNameOptions = useMemo(
    () => assessmentLeases.map((lease) => lease.lease_name).filter(Boolean),
    [assessmentLeases],
  );

  const loadOutput = async () => {
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.(REASSESSMENT_OUTPUT_FILE);
      if (result?.success && Array.isArray(result?.data?.leases)) {
        setOutputLeases(result.data.leases);
      } else {
        setOutputLeases([]);
      }
    } catch {
      setOutputLeases([]);
    }
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: `row_${Date.now()}`,
        lease_name: "",
        factors_changed: [],
        lease_commencement_date: "",
        lease_reassessment_date: "",
        revised_int_ror: 0,
        original_period: 0,
        total_revised_period: 0,
        payment_details: [defaultPaymentRow()],
      },
    ]);
  };

  const removeRow = (id: string) => setRows((prev) => prev.filter((row) => row.id !== id));

  const updateRow = (id: string, updates: Partial<ReassessmentRow>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...updates } : row)));
  };

  const updateLeaseName = (id: string, leaseName: string) => {
    const base = assessmentLeases.find((lease) => lease.lease_name === leaseName);
    updateRow(id, {
      lease_name: leaseName,
      lease_commencement_date: toInputDate(base?.commencement_date || ""),
      revised_int_ror: Number(base?.int_ror) || 0,
      original_period: Number(base?.lease_period) || 0,
      total_revised_period: Number(base?.lease_period) || 0,
    });
  };

  const updatePaymentDetail = (rowId: string, paymentIndex: number, field: keyof PaymentDetailRow, value: string | number) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              payment_details: row.payment_details.map((payment, idx) =>
                idx === paymentIndex ? { ...payment, [field]: value } : payment,
              ),
            }
          : row,
      ),
    );
  };

  const addPaymentDetail = (rowId: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, payment_details: [...row.payment_details, defaultPaymentRow()] } : row)),
    );
  };

  const removePaymentDetail = (rowId: string, paymentIndex: number) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const filtered = row.payment_details.filter((_, idx) => idx !== paymentIndex);
        return { ...row, payment_details: filtered.length ? filtered : [defaultPaymentRow()] };
      }),
    );
  };

  const saveData = async (): Promise<boolean> => {
    if (saveStatus === "saving") return false;
    setSaveStatus("saving");
    try {
      const fyYear = getFY_YEAR();
      if (!fyYear) throw new Error("FY_YEAR is not configured. Please select an engagement.");

      const payload = {
        rows: rows.map((row) => ({
          lease_name: row.lease_name,
          factors_changed: row.factors_changed,
          lease_commencement_date: row.lease_commencement_date ? fromInputDate(row.lease_commencement_date) : "",
          lease_reassessment_date: row.lease_reassessment_date ? fromInputDate(row.lease_reassessment_date) : "",
          revised_int_ror: Number(row.revised_int_ror) || 0,
          original_period: Number(row.original_period) || 0,
          total_revised_period: Number(row.total_revised_period) || 0,
          payment_details: row.payment_details.map((p) => {
            const nonApplicable = NON_APPLICABLE_CLASSIFICATIONS.has(p.payment_classification);
            return {
              payment_classification: p.payment_classification || "",
              payment_type: p.payment_type || "",
              payment_amount: Number(p.payment_amount) || 0,
              n_payments: nonApplicable ? 0 : Number(p.n_payments) || 0,
              int_incr: nonApplicable ? [] : parseCommaSeparatedNumbers(p.int_incr_text),
              incr_per: nonApplicable ? [] : parseCommaSeparatedNumbers(p.incr_per_text),
              month_first_payment: nonApplicable ? 0 : Number(p.month_first_payment) || 0,
            };
          }),
        })),
      };

      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: btoa(JSON.stringify(payload, null, 2)),
        fileName: REASSESSMENT_INPUT_FILE,
        referenceValue: "",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_LEASE_ReassessmentInp"],
      });

      if (!result?.success) throw new Error(result?.error || "Failed to save reassessment input.");
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
      return true;
    } catch (error) {
      console.error("[LeaseReassessmentIndAS] Failed to save:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
      return false;
    }
  };

  const runReassessment = async () => {
    if (isRunning || saveStatus === "saving") return;
    setIsRunning(true);
    try {
      const saved = await saveData();
      if (!saved) throw new Error("Please fix save errors before running.");

      const result = await (window as any).payroll?.run?.("execute_lease_reassessment", {
        inputFiles: [],
        options: { reference_value: "Lease Reassessment" },
      });
      if (!result?.ok) throw new Error(result?.error || "Failed to run lease reassessment.");

      await loadOutput();
    } catch (error) {
      alert(`Failed to run Lease Reassessment: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsRunning(false);
    }
  };

  const openOutput = (leaseName: string) => {
    const match = outputLeases.find(
      (lease) => String(lease?.lease_info?.["Name of Lease"] || "").trim() === String(leaseName || "").trim(),
    );
    if (!match) {
      alert("Output not found for this lease. Please click Run first.");
      return;
    }
    setSelectedOutputLease(match);
    setIsOutputDialogOpen(true);
  };

  const openJournalEntry = async (leaseName: string) => {
    if (!String(leaseName || "").trim()) {
      alert("Select a lease first.");
      return;
    }
    setIsJournalDialogOpen(true);
    setJournalLoading(true);
    setJournalResult(null);
    try {
      const download = async (file: string) => {
        const res = await (window as any).sharePointAPI?.downloadClientJson?.(file);
        return res?.success && res?.data != null ? res.data : null;
      };
      const [assessment, reassessment, modification] = await Promise.all([
        download(ASSESSMENT_OUTPUT_FILE),
        download(REASSESSMENT_OUTPUT_FILE),
        download(MODIFICATION_OUTPUT_FILE),
      ]);
      if (!assessment) {
        setJournalResult({
          ok: false,
          message: `Could not load ${ASSESSMENT_OUTPUT_FILE}. Run Lease Testing and ensure the file exists.`,
        });
        return;
      }
      setJournalResult(computeJournalEntry(leaseName, assessment, reassessment, modification));
    } catch (e) {
      setJournalResult({
        ok: false,
        message: e instanceof Error ? e.message : "Failed to load journal data.",
      });
    } finally {
      setJournalLoading(false);
    }
  };

  const formatJeAmount = (n: number) => formatLeaseDialogCell(n, "Journal");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Lease Reassessment</h2>
          <p className="text-gray-400">Capture lease reassessment changes without altering original lease input</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runReassessment} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={isRunning || saveStatus === "saving"}>
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run
              </>
            )}
          </Button>
          <Button onClick={saveData} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saveStatus === "saving"}>
            {saveStatus === "saving" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          {saveStatus === "saved" && <span className="text-sm text-green-400">Saved!</span>}
          <Button variant="outline" onClick={() => setActiveSection?.("ll-substantive")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <FileCheck2 className="h-5 w-5 text-blue-500" />
              Lease Reassessment Table
            </div>
            <Button onClick={addRow} size="sm" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {rows.length === 0 ? (
            <div className="text-center py-8 text-white/60">No reassessment rows added yet.</div>
          ) : (
            rows.map((row, index) => (
              <div key={row.id} className="border border-white/10 rounded-lg p-4 bg-white/5 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-white font-medium">Lease Reassessment #{index + 1}</h4>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/20 text-white"
                      onClick={() => void openJournalEntry(row.lease_name)}
                      data-allow-archival-edit={true}
                    >
                      Journal Entry
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/20 text-white"
                      onClick={() => openOutput(row.lease_name)}
                      data-allow-archival-edit={true}
                    >
                      Open
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => removeRow(row.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Name of Lease</Label>
                    <Select value={row.lease_name} onValueChange={(value) => updateLeaseName(row.id, value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select lease" />
                      </SelectTrigger>
                      <SelectContent>
                        {leaseNameOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Factor Changed</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-black/50"
                        >
                          <span className="truncate">
                            {row.factors_changed.length > 0 ? row.factors_changed.join(", ") : "Select factors"}
                          </span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] border-white/10 bg-black/90 text-white">
                        {FACTOR_OPTIONS.map((factor) => {
                          const checked = row.factors_changed.includes(factor);
                          return (
                            <DropdownMenuCheckboxItem
                              key={factor}
                              checked={checked}
                              onCheckedChange={(isChecked) => {
                                const next = isChecked
                                  ? [...row.factors_changed, factor]
                                  : row.factors_changed.filter((item) => item !== factor);
                                updateRow(row.id, { factors_changed: next });
                              }}
                            >
                              {factor}
                            </DropdownMenuCheckboxItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Lease Commencement Date</Label>
                    <Input type="date" value={row.lease_commencement_date} disabled className="border-white/10 bg-black/40 text-white opacity-70" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Lease Reassessment Date</Label>
                    <Input type="date" value={row.lease_reassessment_date} onChange={(e) => updateRow(row.id, { lease_reassessment_date: e.target.value })} className="border-white/10 bg-black/40 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Revised Effective Interest Rate</Label>
                    <Input type="number" value={row.revised_int_ror} onChange={(e) => updateRow(row.id, { revised_int_ror: parseFloat(e.target.value) || 0 })} className="border-white/10 bg-black/40 text-white" />
                  </div>
                </div>

                {row.factors_changed.includes("Lease Period") && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-white/10 rounded p-3 bg-black/20">
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Original Period</Label>
                      <Input type="number" value={row.original_period} disabled className="border-white/10 bg-black/40 text-white opacity-70" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Total Revised Period</Label>
                      <Input type="number" value={row.total_revised_period} onChange={(e) => updateRow(row.id, { total_revised_period: parseInt(e.target.value) || 0 })} className="border-white/10 bg-black/40 text-white" />
                    </div>
                  </div>
                )}

                <div className="border border-white/10 rounded-lg p-3 bg-black/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="text-white font-medium">Fresh Payment Input Table</h5>
                    <Button onClick={() => addPaymentDetail(row.id)} size="sm" className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Row
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-white/10">
                      <thead>
                        <tr className="bg-black/60">
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Classification</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Type</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Amount (per month/year)</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">No. of months</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Interval of Increment</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Increment %</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Month of First Payment</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.payment_details.map((payment, paymentIndex) => {
                          const nonApplicable = NON_APPLICABLE_CLASSIFICATIONS.has(payment.payment_classification);
                          return (
                            <tr key={`${row.id}_${paymentIndex}`} className="hover:bg-white/5">
                              <td className="border border-white/10 px-2 py-2">
                                <Select value={payment.payment_classification} onValueChange={(value) => updatePaymentDetail(row.id, paymentIndex, "payment_classification", value)}>
                                  <SelectTrigger><SelectValue placeholder="Select classification" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Fixed Payment">Fixed Payment</SelectItem>
                                    <SelectItem value="Variable Payment">Variable Payment</SelectItem>
                                    <SelectItem value="Guaranteed Residual Value">Guaranteed Residual Value</SelectItem>
                                    <SelectItem value="Purchase Price">Purchase Price</SelectItem>
                                    <SelectItem value="Penalty for Termination">Penalty for Termination</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input value={payment.payment_type} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "payment_type", e.target.value)} className="border-white/10 bg-black/40 text-white" />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input type="number" value={payment.payment_amount} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "payment_amount", parseFloat(e.target.value) || 0)} className="border-white/10 bg-black/40 text-white" />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input type="number" value={payment.n_payments} disabled={nonApplicable} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "n_payments", parseInt(e.target.value) || 0)} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input value={payment.int_incr_text} disabled={nonApplicable} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "int_incr_text", e.target.value)} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input value={payment.incr_per_text} disabled={nonApplicable} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "incr_per_text", e.target.value)} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Input type="number" value={payment.month_first_payment} disabled={nonApplicable} onChange={(e) => updatePaymentDetail(row.id, paymentIndex, "month_first_payment", parseInt(e.target.value) || 0)} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                              </td>
                              <td className="border border-white/10 px-2 py-2">
                                <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => removePaymentDetail(row.id, paymentIndex)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={isJournalDialogOpen} onOpenChange={setIsJournalDialogOpen}>
        <DialogContent className="max-w-3xl w-[95vw] bg-black border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Lease Reassessment (journal entry)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            {journalLoading ? (
              <div className="flex items-center gap-2 text-white/80">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading assessment, reassessment, and modification outputs…
              </div>
            ) : !journalResult ? (
              <div className="text-white/60">No data.</div>
            ) : !journalResult.ok ? (
              <div className="text-amber-200/90">{journalResult.message}</div>
            ) : (
              <>
                <p className="text-white/70">
                  Source: <span className="text-white">{journalResult.sourceLabel}</span> — event month{" "}
                  <span className="text-white">{journalResult.eventMonth}</span> (opening{" "}
                  {formatJeAmount(journalResult.openingEvent)}) vs assessment closing{" "}
                  <span className="text-white">{journalResult.priorMonth}</span> (
                  {formatJeAmount(journalResult.closingPriorAssessment)}).
                </p>
                {journalResult.amount === 0 ? (
                  <p className="text-white/80">No journal adjustment required (difference rounds to zero).</p>
                ) : (
                  <div className="overflow-x-auto rounded border border-white/10">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-black/60 border-b border-white/10">
                          <th className="text-left px-3 py-2 text-white font-medium">Particulars</th>
                          <th className="w-16 px-2 py-2 text-white font-medium" />
                          <th className="text-right px-3 py-2 text-white font-medium min-w-[7rem]">Amount</th>
                          <th className="text-right px-3 py-2 text-white font-medium min-w-[7rem]">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="text-white">
                        <tr className="border-b border-white/5">
                          <td colSpan={4} className="px-3 py-1 h-2 bg-transparent" aria-hidden />
                        </tr>
                        <tr>
                          <td className="px-3 py-2 align-top">
                            {journalResult.delta > 0 ? "Right of Use Asset" : "Lease Liability"}
                          </td>
                          <td className="px-2 py-2 align-top text-white/90">Dr</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatJeAmount(journalResult.amount)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-white/30">—</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 pl-6 align-top text-white/90">
                            To {journalResult.delta > 0 ? "Lease Liability" : "Right of Use Asset"}
                          </td>
                          <td className="px-2 py-2 align-top" />
                          <td className="px-3 py-2 text-right tabular-nums text-white/30">—</td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatJeAmount(journalResult.amount)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isOutputDialogOpen} onOpenChange={setIsOutputDialogOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] bg-black border-white/20 text-white overflow-hidden">
          <DialogHeader>
            <DialogTitle>Lease Reassessment Output - {selectedOutputLease?.lease_info?.["Name of Lease"] || ""}</DialogTitle>
          </DialogHeader>
          <div className="h-full overflow-auto space-y-4 pr-1">
            {!selectedOutputLease ? (
              <div className="text-white/60">No lease output selected.</div>
            ) : (
              <>
                <OutputTable rows={[selectedOutputLease.lease_info || {}]} />
                <OutputTable rows={selectedOutputLease.payment_details || []} />
                <OutputTable rows={selectedOutputLease.schedule || []} />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OutputTable({ rows }: { rows: Array<Record<string, any>> }) {
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  if (headers.length === 0) return <div className="text-white/60">No data available.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border border-white/10">
        <thead>
          <tr className="bg-black/60">
            {headers.map((header) => (
              <th key={header} className="border border-white/10 px-3 py-2 text-left text-white text-sm whitespace-nowrap">
                {header || " "}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-white/5">
              {headers.map((header) => (
                <td key={`${idx}-${header}`} className="border border-white/10 px-3 py-2 text-white text-sm whitespace-nowrap">
                  {formatLeaseDialogCell(row[header], header)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function parseCommaSeparatedNumbers(value: string): number[] {
  if (!value || !value.trim()) return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => Number(v))
    .filter((v) => !Number.isNaN(v));
}

function toInputDate(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  const parts = value.includes("/") ? value.split("/") : [];
  if (parts.length === 3) {
    const month = parts[0].padStart(2, "0");
    const day = parts[1].padStart(2, "0");
    const year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
    return `${year}-${month}-${day}`;
  }
  return "";
}

function fromInputDate(inputDate: string): string {
  if (!inputDate) return "";
  const [year, month, day] = inputDate.split("-");
  return `${month}/${day}/${year}`;
}
